<template>
  <div style="background-color:black;">
    <div>
      <!-- Header Section -->
      <v-row class="pa-2">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <v-btn icon class="back-button my-4 mx-4" @click="confirmBackNavigation">
            <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
          </v-btn>
          <!-- Conditionally hide the Add Delivery button if the role is Salesperson -->
          <v-btn 
          
            color="#ff3b47" 
            width="150px" 
            rounded 
            dense 
            class="add-delivery-button mx-4" 
            dark 
            @click="navigateToAddDelivery"
          >
            Add Delivery
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loader while fetching data -->
      <v-row v-if="loading" justify="center" align="center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-row>

      <!-- Deliveries Section -->
      <div v-if="!loading" style="background-color:white; border-radius:20px 20px 0 0; padding: 16px;">
        <!-- Search and Filter -->
        <v-row justify="center">
          <v-col cols="12" md="4">
            <v-text-field
              class="pa-3 white-search-field"
              filled
              dense
              rounded
              v-model="search"
              append-icon="mdi-magnify"
              label="Search Deliveries"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedMethod"
              :items="[...methodOptions, 'Clear filter']"
              placeholder="Filter by Method"
              filled
              dense
              rounded
              class="my-3"
              @change="handleSelectChange"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Deliveries Cards -->
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="3" v-for="(delivery, index) in paginatedDeliveries" :key="index">
            <v-card class="modern-card" elevation="3" hover @click="handleCardClick(delivery)">
              <!-- Header with Urgency, Status, and Delivery Number -->
              <v-row class="d-flex justify-end pr-3 pt-3">
                <!-- Urgency Square -->
                <div
                  :class="['urgency-square', getUrgencySquareColor(delivery.urgency)]"
                ></div>

                <!-- Status Chip -->
                <v-chip 
                  :color="getChipColor(delivery.status)" 
                  dark 
                  label
                  class="white--text ml-2"
                >
                  {{ delivery.status || 'Unknown' }}
                </v-chip>
                
                <!-- Delivery Number Chip -->
                <v-chip 
                  color="grey" 
                  dark 
                  label
                  class="white--text ml-2"
                >
                  No.{{ delivery.deliveryNumber || 'Unknown' }}
                </v-chip>
              </v-row>
              
              <!-- Card Content -->
              <v-card-text class="pa-4">
                <!-- Date Aligned to Right -->
                <v-row justify="end">
                  <v-col cols="12" class="py-2 text-right">
                    <span class="text-caption">Date:</span>
                    <span class="font-weight-medium">{{ formatDate(delivery.date) || 'Unknown' }}</span>
                  </v-col>
                </v-row>

                <!-- Delivery Details -->
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Priority:</span>
                    <span class="font-weight-medium">{{ delivery.urgency || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Driver Name:</span>
                    <span class="font-weight-medium">{{ delivery.driverName || 'N/A' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Location:</span>
                    <span class="font-weight-medium">{{ delivery.location || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Method:</span>
                    <span class="font-weight-medium">{{ delivery.method || 'Unknown' }}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-2">
                    <span class="text-caption">Type:</span>
                    <span class="font-weight-medium">{{ delivery.type || 'N/A' }}</span>
                  </v-col>
                </v-row>
              </v-card-text>

              <!-- Card Actions -->
              <v-card-actions>
                <v-btn 
                  width="100%" 
                  rounded 
                  dark 
                  color="#ff3b47" 
                  @click.stop="navigateTo(delivery)"
                >
                  Open Delivery
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Load More Button -->
        <v-row justify="center" v-if="canLoadMore">
          <v-btn 
            color="#ff3b47" 
            @click="loadMore" 
            :disabled="!canLoadMore"
            class="load-more-button"
          >
            Load More
          </v-btn>
        </v-row>
      </div>
    </div>

    <!-- Confirmation Dialog for Back Button -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="green" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Salesperson Delivery Detail Dialog -->
    <v-dialog v-model="salespersonDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Delivery Details</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <p><strong>Delivery Number:</strong> {{ selectedDelivery.deliveryNumber || 'Unknown' }}</p>
              <p><strong>Date:</strong> {{ formatDate(selectedDelivery.date) || 'Unknown' }}</p>
              <p><strong>Driver Name:</strong> {{ selectedDelivery.driverName || 'N/A' }}</p>
              <p><strong>Items:</strong> {{ selectedDelivery.items ? selectedDelivery.items.length : 0 }}</p>
              <p><strong>Method:</strong> {{ selectedDelivery.method || 'Unknown' }}</p>
              <p><strong>Status:</strong> {{ selectedDelivery.status || 'Unknown' }}</p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="salespersonDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      deliveries: [],
      search: '',
      selectedMethod: '',
      methodOptions: ['Courier', 'Driver', 'Both'],
      userRole: '', // To store the logged-in user's role
      salespersonDialog: false, // To control the dialog for Salesperson
      selectedDelivery: {}, // To store the delivery details to be shown in the dialog
      loading: true, // To show the loader while fetching data
      backDialog: false, // To control the back confirmation dialog
      visibleCount: 9, // Initial number of visible items
      loadIncrement: 9, // Number of items to load each time
    };
  },
  computed: {
    isSalesperson() {
      return this.userRole === 'Salesperson';
    },
    filteredDeliveries() {
    let filtered = this.deliveries;

    if (this.search) {
      const searchLower = this.search.toLowerCase();
      filtered = filtered.filter(delivery =>
        (delivery.deliveryNumber && delivery.deliveryNumber.toString().toLowerCase().includes(searchLower)) ||
        (delivery.method && delivery.method.toLowerCase().includes(searchLower)) ||
        (delivery.date && this.formatDate(delivery.date).toLowerCase().includes(searchLower)) ||
        (delivery.driverName && delivery.driverName.toLowerCase().includes(searchLower))
      );
    }

    if (this.selectedMethod && this.selectedMethod !== 'Clear filter') {
      filtered = filtered.filter(delivery => delivery.method === this.selectedMethod);
    }

    // Exclude deliveries with status 'Complete' or 'Completed'
    filtered = filtered.filter(delivery => {
      const status = delivery.status ? delivery.status.toLowerCase() : '';
      return status !== 'complete' && status !== 'completed';
    });

    // Sort by deliveryNumber from highest to lowest
    filtered.sort((a, b) => b.deliveryNumber - a.deliveryNumber);

    return filtered;
  },
    paginatedDeliveries() {
      return this.filteredDeliveries.slice(0, this.visibleCount);
    },
    canLoadMore() {
      return this.visibleCount < this.filteredDeliveries.length;
    },
  },
  watch: {
    search() {
      this.resetPagination();
    },
    selectedMethod() {
      this.resetPagination();
    },
  },
  async created() {
    console.log("Component created. Fetching user details...");
    await this.fetchUserDetails();
    console.log("User details fetched. Fetching deliveries...");
    await this.fetchDeliveries();
    console.log("Deliveries fetched:", this.deliveries);
  },
  methods: {
    /**
     * Fetch the current user's role from Firestore.
     */
     async fetchUserDetails() {
      try {
        const user = auth.currentUser;
        if (user) {
          this.userEmail = user.email; // Store user email
          const uid = user.uid;
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
            this.userRole = userDoc.data().role || '';
          }
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    },

    async fetchDeliveries() {
      try {
        if (!this.userEmail) return;

        // Query for deliveries assigned to current user's email
        const deliveriesQuery = query(
          collection(db, "deliveries"),
          where("assignedTo.email", "==", this.userEmail)
        );

        const querySnapshot = await getDocs(deliveriesQuery);
        this.deliveries = querySnapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            deliveryNumber: data.deliveryNumber,
            method: data.method,
            location: data.location || 'Unknown',
            driverName: data.driverName || 'N/A',
            status: data.status || 'Unknown',
            items: data.deliveryItems || [],
            type: data.type || "",
            urgency: data.urgency || 'Unknown',
            date: data.date || 'Unknown',
            assignedTo: data.assignedTo || {} // Ensure assignedTo exists
          };
        });

        // Client-side filtering for status
        this.deliveries = this.deliveries.filter(delivery => {
          const status = delivery.status.toLowerCase();
          return status !== 'complete' && status !== 'completed';
        });

      } catch (error) {
        console.error("Error fetching deliveries:", error);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Handle changes in the method filter.
     * @param {String} value - The selected method filter.
     */
    handleSelectChange(value) {
      console.log(`Method filter changed to: ${value}`);
      if (value === 'Clear filter') {
        this.clearFilters();
      }
    },
    /**
     * Open confirmation dialog before navigating back.
     */
    confirmBackNavigation() {
      console.log("Back navigation confirmed.");
      this.backDialog = true;
    },
    /**
     * Navigate back in history.
     */
    goBack() {
      console.log("Navigating back.");
      this.backDialog = false;
      this.$router.go(-1);
    },
    /**
     * Handle card click based on user role.
     * @param {Object} delivery - The delivery object.
     */
    handleCardClick(delivery) {
      console.log("Card clicked for delivery:", delivery);
      if (this.isSalesperson) {
        this.selectedDelivery = delivery;
        this.salespersonDialog = true;
        console.log("Opening Salesperson Dialog for delivery:", delivery);
      } else {
        this.navigateTo(delivery);
      }
    },
    /**
     * Navigate to the Edit Deliveries page with the selected delivery.
     * @param {Object} delivery - The delivery object.
     */
    navigateTo(delivery) {
      console.log("Navigating to EditDeliveries for delivery ID:", delivery.id);
      if (delivery.id) {
        localStorage.setItem('selectedDelivery', delivery.id);
        this.$router.push({ name: 'EditDeliveries' });
      } else {
        console.warn("Delivery ID is missing. Cannot navigate.");
      }
    },
    /**
     * Navigate to the Add Deliveries page.
     */
    navigateToAddDelivery() {
      console.log("Navigating to Add Deliveries page.");
      this.$router.push({ path: '/addDeliveries' });
    },
    /**
     * Clear all filters.
     */
    clearFilters() {
      console.log("Clearing all filters.");
      this.selectedMethod = '';
      this.search = '';
    },
    /**
     * Get the color class for the urgency square based on urgency level.
     * @param {String} urgency - The urgency level.
     * @returns {String} - The color class.
     */
    getUrgencySquareColor(urgency) {
      switch (urgency) {
        case 'Urgent':
          return 'red-square';
        case 'Medium':
          return 'orange-square';
        case 'Not urgent':
          return 'green-square';
        default:
          return 'grey-square';
      }
    },
    /**
     * Get the color for the status chip based on status.
     * @param {String} status - The status of the delivery.
     * @returns {String} - The color string.
     */
    getChipColor(status) {
      switch(status.toLowerCase()) { // Ensure case-insensitive comparison
        case 'completed':
        case 'complete': // Handle different status naming
          return 'green';
        case 'pending':
          return 'orange';
        case 'delayed':
          return 'red';
        case 'in queue':
          return 'blue';
        case 'in transit':
          return 'purple';
        case 'booked':
          return 'teal';
        default:
          return 'grey';
      }
    },
    /**
     * Format the date string into a readable format.
     * @param {String|Date} dateStr - The date string in 'YYYY-MM-DD' format or timestamp.
     * @returns {String} - The formatted date string.
     */
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'Unknown') return 'Unknown';
      let date;
      if (typeof dateStr === 'string') {
        date = new Date(dateStr);
      } else if (dateStr instanceof Date) {
        date = dateStr;
      } else {
        date = new Date();
      }
      if (isNaN(date)) return dateStr; // Return as is if invalid date
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    /**
     * Load more deliveries by increasing the visible count.
     */
    loadMore() {
      console.log("Loading more deliveries.");
      this.visibleCount += this.loadIncrement;
    },
    /**
     * Reset the pagination to initial state.
     */
    resetPagination() {
      console.log("Resetting pagination.");
      this.visibleCount = this.loadIncrement;
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

body, div, span, v-card-title, v-card-text, v-row, v-col, v-card, v-chip, v-avatar {
  font-family: 'Montserrat', sans-serif;
}

.modern-card {
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  cursor: pointer;
  margin: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.modern-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.v-btn {
  font-size: 0.8rem;
  text-transform: none;
}

.v-card-actions {
  padding: 0 16px 16px;
  display: flex;
  justify-content: center;
}

/* Custom styling for white search field */
.white-search-field .v-input__control {
  background-color: white;
  border-radius: 4px;
}

.white-search-field .v-label {
  color: black;
}

.white-search-field .v-input__icon--append .v-icon {
  color: black;
}

/* Urgency Square Styling */
.urgency-square {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.red-square {
  background-color: red;
}

.orange-square {
  background-color: orange;
}

.green-square {
  background-color: green;
}

.grey-square {
  background-color: grey;
}

/* Status Chip Styling */
.v-chip.status-chip {
  font-size: 0.75rem;
  text-transform: capitalize;
}

/* Delivery Number Chip Styling */
.v-chip.delivery-number-chip {
  font-size: 0.75rem;
  text-transform: uppercase;
}

/* Open Delivery Button Styling */
.open-delivery-button {
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.open-delivery-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

/* Load More Button Styling */
.load-more-button {
  font-weight: 600;
  margin-top: 16px;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.load-more-button:hover {
  background-color: #e63946;
  transform: scale(1.02);
}

/* Back button styling */
.back-button {
  color: white;
  border-radius: 50%;
  transition: transform 0.3s ease-in-out;
}

.back-button:hover {
  transform: scale(1.1);
}

.back-button .v-icon {
  color: white;
}

/* Add Delivery button styling */
.add-delivery-button {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
}

/* Modern card text styling */
.text-caption {
  font-size: 0.75rem;
  color: #757575;
}

.font-weight-medium {
  font-weight: 500;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .modern-card {
    margin: 8px;
    height: auto; /* Adjusted to auto for better responsiveness */
  }
  .v-col {
    margin-bottom: 16px;
  }
}
</style>
